import type {
  AccountKind,
  RegistrationLookup,
  RegistrationNumber,
  RegistrationRunStatus,
  RegistrationStep
} from '@/domains/auth';
import type { IMyTeamRouting, TeamRoutingOutcome, TeamRoutingSource } from '@/domains/survey';
import type { AccountStatus, IAdminAccountActivity } from './account-contracts';
import type { IIdentifierCheck } from './identifier-check-contracts';
import type { IParticipantInternalRecord } from './internal-field-contracts';
import type {
  IOversightResponseRow,
  IOversightUsage,
  IParticipantRef,
  IStaffNote,
  ResponseStatus,
  SubjectKind
} from './oversight-contracts';

/**
 * The participant surface — survey-api's advisor profile (SR-213 block 8: E1–E3), the
 * registration queue that shipped before it (A6), the account links block 6 added (F3), the
 * register firms a participant's numbers name (SR-214's `secAdvFirms`), the person hub
 * SR-215 built over them — the profile, the BrokerCheck summaries, the household read, its
 * activity and the People list — a team's routing and archive stamp on the card with the
 * three writes on them (SR-213's Teams block), and the disclosures of a participant's CRD set
 * with its archived Salesforce tasks (SR-216), and the record search over them (SR-414: People
 * persons only, the Teams and Firms lists, a roster row's card, why a row matched, a person's
 * teams and firms) — as types.
 *
 * Source of truth: `survey-api/src/Veritant.Surveys.Api/Endpoints/AdminParticipantEndpoints.cs`,
 * `AdminPeopleEndpoints.cs` and `AdminRegistrationEndpoints.cs`,
 * `Infrastructure/External/BrokerCheckFactProjections.cs`, and the plans they implement
 * (`survey-api/docs/archive/plans/2026-09-23-advisor-profile.md`, R1–R14,
 * `2026-09-24-self-service-gaps.md`, R1–R18, `2026-09-24-sec-adv-register-browse.md`, R6,
 * `docs/superpowers/specs/2026-09-25-person-hub-backend-design.md`,
 * `docs/superpowers/specs/2026-09-28-disclosures-design.md`, §6, and
 * `docs/superpowers/specs/2026-10-12-record-search-design.md`, §3 and §6). Where prose and C#
 * disagree the C# wins; every name below is the wire's, checked against a real read.
 *
 * Four decisions shape this file:
 *
 * - **The detail is the row's strict superset, and says so by inheriting.** survey-api builds
 *   `AdminParticipantDetailDto` from the list row's eight members under the same names, and
 *   each number on it from the row's number, and pins both with a contract test — the way it
 *   pins the account pair `account-contracts.ts` inherits. So `IAdminParticipantDetail extends
 *   IAdminParticipantRow`, and anything that renders a row renders a card with no branch.
 * - **The account on the card does not inherit anything.** `IAdminParticipantAccount` shares
 *   most of its scalar members with `IAdminAccountDetail`, and is still its own interface: it
 *   has **no `idp`** (how the account signs in stays on the admin card), and its
 *   `participants` are refs, narrower than the account card's elements of that name. Reusing
 *   the account type would let a screen read a member this document does not carry — and `idp`
 *   read as absent is `undefined`, which a `=== null` test takes for "not linked", the one fact
 *   this document withholds.
 * - **A registry's payload is `unknown`; its summary is the server's reading, and typed.**
 *   `payload` is the stored document, verbatim (roadmap D8): nothing on this side knows its
 *   shape, and a type claiming one would be a guess about FINRA's JSON that compiles.
 *   `summary` is survey-api's projection of it (person hub design §5), a shape survey-api owns
 *   and declares per `factKey` — so it is typed here as the projection's records spell it, and
 *   reached only through `brokerCheckSummary`, which narrows on the key. A summary under a key
 *   this app has not typed stays `unknown` and is drawn whole, as a payload is.
 * - **Nothing here says which registry a number belongs to.** survey-api declined a
 *   `registry` member (its R5) and named the grouping over `idType` the client's; it is done
 *   where the hub renders, not by a field invented here.
 */

/**
 * `FactStatus`, as `WireEnum` spells it — what a registry answered. The auth domain's
 * `RegistrationLookup` is this vocabulary on a registration view; it is aliased rather than
 * spelled a second time, so the two cannot drift.
 */
export type FactStatus = RegistrationLookup;

/** `participant_identifier.verified_via` — survey-api's `VerificationSources`. Null on a number
 *  nobody has stamped. */
export type VerificationSource = 'registry' | 'staff';

/** `participant_identifier.self_confirmation` — the owner's answer on the catalogue's identifier
 *  card. Null until they answer. */
export type SelfConfirmation = 'confirmed' | 'denied';

/** An `external_fact_lookup` job that has not finished. Only these two are ever sent: a job that
 *  finished is a fact, or nothing. */
export type PendingLookupStatus = 'queued' | 'running';

/** `AccountLinkService.Relations`: an account's link to a participant — `self` its own;
 *  `contact` a point of contact an invitation made (SR-416); `member` listed on a team's or
 *  firm's roster, linked by the roster (SR-487, PFR §5); `assistant` an advisor's assistant,
 *  linked by staff or the advisor's invitation (PFR §8). Open, so an unknown one still reads. */
export type AccountRelation = 'self' | 'contact' | 'member' | 'assistant' | (string & {});

// ---------------------------------------------------------------------------
// The participant row — a People row's participants, and the card's base
// ---------------------------------------------------------------------------

/**
 * One number on a list row — `AdminParticipantListIdentifierDto`. `verified` rather than the
 * instant: the row says whether, the card says when and by whom.
 */
export interface IAdminParticipantListIdentifier {
  /** `CRD`, `ORG_CRD`, `NPN`, `SEC_NUMBER`, `EMAIL`, a migrated type — not a closed set here:
   *  the migration carried types no screen has words for, and they still render. */
  idType: string;
  value: string;
  verified: boolean;
}

/**
 * One participant on the list — `AdminParticipantListItemDto`. The numbers are on the row
 * because two advisors of one name differ by nothing else it shows.
 */
export interface IAdminParticipantRow {
  participantId: string;
  kind: SubjectKind;
  displayName: string;
  photoUri: Nullable<string>;
  createdAt: string;
  /**
   * No number of this participant carries a `verified_at`. True of every migrated row, so it
   * is the normal state rather than an alarm — which is why survey-api offers no filter on it.
   */
  unverified: boolean;
  /** The owner denied at least one of these numbers — the queue `?denied=true` selects. */
  denied: boolean;
  identifiers: IAdminParticipantListIdentifier[];
  /** What the participant's own answers say about it (SR-215). Always sent; see the type. */
  profile: IAdminParticipantProfile;
  /**
   * The participant holds a work item that is `pending_firm` or `escalated` — survey-api's
   * `WorkItemStates.UnderReview`, decided there and drawn here as sent (SR-222 block 10). On the
   * list item and so on the card, the hub's members and People's participants, which are all
   * this row. Optional because an API from before that block omits it, and a member read
   * without it draws no chip — never "not under review".
   */
  underReview?: boolean;
  /**
   * What the record is to survey-api (SR-391's B1, its `RecordTypes.Classify`): a `person`; an org
   * that holds a Teams response or a routing decision, a `team`; one that holds an RIA_FIRM
   * response, a `firm`; any other org, `org`. Survey-api's rule, read and never derived here — it
   * picks which record page draws the participant, and which list's address a link names
   * (SR-414). On the card since SR-391 and on the list row since SR-414. **Absent** from a
   * survey-api before those, which draws a team or a firm on the household's page, and links it
   * under People.
   */
  recordType?: RecordType;
}

/**
 * A participant on a list — `AdminParticipantListItemDto` as the participant list (the ranking
 * insert's picker) and People's `participants` send it: the row, and `matches` — why `q` found it
 * (record search §6), `[]` without `q` and on People's participants. The one member the card does
 * not carry, so it is its own type rather than the row's: the card stays the row's superset.
 * **Absent** from a survey-api before SR-414 (the plan's D4): read `?? []`.
 */
export interface IAdminParticipantListItem extends IAdminParticipantRow {
  matches?: ISearchMatch[];
}

/**
 * The participant as its questionnaires and its registration name it —
 * `AdminParticipantProfileDto` (person hub design §2): each member projected by survey-api from
 * a bound usage's answer.
 *
 * **Every member is null where nothing filled it** (the four newer ones absent too, from an API
 * before them), and the object itself is always present. An
 * object of nulls is how a screen tells a questionnaire's name from `displayName`, which for a
 * migrated participant may be a login standing in for one — and it is said as that ("nothing
 * has named this participant yet"), never read as "this is a login", which no member says.
 */
export interface IAdminParticipantProfile {
  firstName: Nullable<string>;
  lastName: Nullable<string>;
  preferredName: Nullable<string>;
  firmName: Nullable<string>;
  teamName: Nullable<string>;
  contactName: Nullable<string>;
  city: Nullable<string>;
  state: Nullable<string>;
  officeEmail: Nullable<string>;
  phone: Nullable<string>;
  /** The four survey-api added with SR-225's profile members (the firm's legal name, a second
   *  phone and two more addresses) — absent from an API before them, drawn only where sent. */
  legalFirmName?: Nullable<string>;
  cellPhone?: Nullable<string>;
  altEmail?: Nullable<string>;
  ccEmail?: Nullable<string>;
  /** Every survey's zip code (SR-414's D12) — absent from an API before it. */
  postalCode?: Nullable<string>;
}

// ---------------------------------------------------------------------------
// GET /admin/participants/{id} — the card; this app reads it only inside the hub's document
// ---------------------------------------------------------------------------

/** The member of staff who stamped a number — an address as well as the id, so the hub names
 *  a person. */
export interface IAdminVerifier {
  accountId: string;
  email: string;
}

/**
 * One number in full — `AdminParticipantIdentifierDto`, the list's number plus seven members.
 * Ordered by survey-api as `/my/identifiers` orders them: `idType`, `value`, `identifierId`.
 */
export interface IAdminParticipantIdentifier extends IAdminParticipantListIdentifier {
  identifierId: string;
  /** Where the row came from — `registration`, `migration` — which changes what a hand
   *  verification means against it. Passed through as stored. */
  source: string;
  createdAt: string;
  verifiedAt: Nullable<string>;
  verifiedVia: Nullable<VerificationSource>;
  /** Set for a `staff` stamp only. Null for a registry stamp and for no stamp alike —
   *  `verifiedVia` is what tells those two apart. */
  verifiedBy: Nullable<IAdminVerifier>;
  selfConfirmation: Nullable<SelfConfirmation>;
}

/**
 * The latest answer one registry gave under one key — the newest `external_fact` per
 * `(sourceSystem, factKey)`, from every source, `prior_cycle.summary` included.
 *
 * An `error` is stored and sent like any other answer: it says the registry could not be asked
 * at `fetchedAt`, and survey-api never retries one by itself.
 */
export interface IAdminParticipantFact {
  /** `brokercheck`, `iapd`, `sec_adv`, `nipr`, `prior_cycle`, … — whichever wrote it. */
  sourceSystem: string;
  /** `brokercheck.person`, `iapd.person`, `adv.firm_profile`, `prior_cycle.summary`, … */
  factKey: string;
  status: FactStatus;
  /** The registry's own date for what it said, when it gave one. */
  asOf: Nullable<string>;
  fetchedAt: string;
  /** Verbatim (roadmap D8). Rendered whole, never picked apart — under the summary, folded. */
  payload: unknown;
  /**
   * survey-api's typed reading of `payload`, computed on read (person hub design §5.1). Null
   * for any key but `brokercheck.person`, `brokercheck.firm` and (SR-396) `iapd.person` — IAPD's
   * record read as a person's, from under `iacontent` — for an answer that is not `ok`, and for
   * one that is not exactly one hit — two documents are two people. `unknown` because survey-api
   * sends one `JsonElement` for every key: `brokerCheckSummary` is the one way to a typed reading.
   */
  summary: Nullable<unknown>;
}

// ---------------------------------------------------------------------------
// A fact's summary — BrokerCheckFactProjections' records
// ---------------------------------------------------------------------------

/**
 * FINRA's name against the participant's **profile** (never `displayName`, which may be a
 * login): `unknown` while either side has no name to compare. A reading, not a verification —
 * nothing is stamped by a `match`.
 */
export type NameMatch = 'match' | 'differs' | 'unknown';

/** FINRA's `Y` / `N`, passed through as it sent them — not read as a boolean here. */
export type FinraFlag = string;

/**
 * One employment — `Employment`. Brokerage and IA lists are concatenated by survey-api, in that
 * order; a previous one's list is ordered newest end first. Dates are ISO days.
 */
export interface IBrokerCheckEmployment {
  firm_id?: number;
  firm_name?: string;
  ia_only?: FinraFlag;
  bd_sec_number?: string;
  /** `801-NNNNN`, joined by survey-api from FINRA's split type and number. */
  ia_sec_number?: string;
  city?: string;
  state?: string;
  registration_begin?: string;
  /** Never sent on a current employment, even where FINRA gave one. */
  registration_end?: string;
  bc_scope?: string;
  ia_scope?: string;
}

export interface IBrokerCheckRegisteredState {
  state?: string;
  scope?: string;
  status?: string;
  since?: string;
}

export interface IBrokerCheckExamCounts {
  state?: number;
  principal?: number;
  product?: number;
}

export interface IBrokerCheckExam {
  category?: string;
  name?: string;
  taken?: string;
  scope?: string;
}

export interface IBrokerCheckRegistrationCount {
  sro?: number;
  finra?: number;
  state?: number;
}

export interface IBrokerCheckBrokerDetails {
  has_bc_comments?: FinraFlag;
  has_ia_comments?: FinraFlag;
  report_status?: string;
}

/**
 * `brokercheck.person`'s summary — `PersonSummary`, and `iapd.person`'s since SR-396 (survey-api
 * projects IAPD's record with the same `Person`). The wire's snake_case, kept rather than
 * renamed: it is survey-api's document, and a mapping here would be a second spelling of it.
 *
 * **A scalar or object member is absent where FINRA sent nothing** (a date absent where it was
 * not FINRA's `M/d/yyyy`); **every list is present**, `[]` when FINRA sent none. So an absent
 * member is "not answered", never a zero.
 */
export interface IBrokerCheckPersonSummary {
  individual_id?: number;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  other_names: string[];
  bc_scope?: string;
  ia_scope?: string;
  days_in_industry?: number;
  current_employments: IBrokerCheckEmployment[];
  previous_employments: IBrokerCheckEmployment[];
  disclosure_flag?: FinraFlag;
  ia_disclosure_flag?: FinraFlag;
  disclosure_count?: number;
  registered_states: IBrokerCheckRegisteredState[];
  /** FINRA's own elements, copied whole by survey-api — not projected, so not typed here. */
  registered_sros: unknown[];
  exams?: IBrokerCheckExamCounts;
  exam_categories: IBrokerCheckExam[];
  registration_count?: IBrokerCheckRegistrationCount;
  broker_details?: IBrokerCheckBrokerDetails;
  name_match: NameMatch;
}

/** Form CRS as FINRA has it on file for a firm. */
export interface IBrokerCheckCrs {
  type?: string;
  file_id?: string;
}

/** The firm's IA office — the one an IA-only firm carries. */
export interface IBrokerCheckOfficeAddress {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
}

/** `brokercheck.firm`'s summary — `FirmSummary`, under the person summary's rules. Its
 *  `name_match` compares every name FINRA gives the firm with the display name and the
 *  profile's firm name. */
export interface IBrokerCheckFirmSummary {
  firm_id?: number;
  firm_name?: string;
  other_names: string[];
  bc_scope?: string;
  ia_scope?: string;
  is_ia_firm?: FinraFlag;
  firm_type?: string;
  firm_status?: string;
  ia_sec_number?: string;
  bd_sec_number?: string;
  crs?: IBrokerCheckCrs;
  office_address?: IBrokerCheckOfficeAddress;
  bd_disclosure_flag?: FinraFlag;
  ia_disclosure_flag?: FinraFlag;
  report_status?: string;
  name_match: NameMatch;
}

/** A summary this app has a type for, tagged by the key survey-api projected it under. */
export type BrokerCheckSummary =
  | { factKey: 'brokercheck.person' | 'iapd.person'; summary: IBrokerCheckPersonSummary }
  | { factKey: 'brokercheck.firm'; summary: IBrokerCheckFirmSummary };

/**
 * The one way from a fact's `summary` to a typed one: by `factKey`, the key survey-api chose
 * the projection by. Null for a fact with no summary and for any key this app has not typed —
 * whose summary is still drawn, whole, as `unknown`.
 *
 * Nothing is checked inside the document: its shape is survey-api's, declared by the key, and
 * a member-by-member test here would be this app reading FINRA's record a second time.
 */
export function brokerCheckSummary(fact: IAdminParticipantFact): Nullable<BrokerCheckSummary> {
  if (fact.summary === null || typeof fact.summary !== 'object') return null;
  switch (fact.factKey) {
    case 'brokercheck.person':
    case 'iapd.person':
      return { factKey: fact.factKey, summary: fact.summary as IBrokerCheckPersonSummary };
    case 'brokercheck.firm':
      return { factKey: fact.factKey, summary: fact.summary as IBrokerCheckFirmSummary };
    default:
      return null;
  }
}

/**
 * A lookup asked for and not yet answered — "pressed, waiting". Addressed by the number it asks
 * about, which is how a screen matches it to one: `factKey` would need the idType → key mapping
 * a second time.
 */
export interface IAdminParticipantPendingLookup {
  factKey: string;
  idType: string;
  value: string;
  status: PendingLookupStatus;
  /** When it was enqueued. */
  requestedAt: string;
}

/**
 * One account acting for the participant, with its login state — `AdminParticipantAccountDto`.
 *
 * Its scalar members are `IAdminAccountDetail`'s under the same names and spellings, and it
 * deliberately does not extend that type — see this file's header. What it withholds is
 * `idp`; what it adds is `relation`, this participant's link to the account.
 */
export interface IAdminParticipantAccount {
  accountId: string;
  email: string;
  /**
   * The account's kind (SR-228), where `role` was (the role-model UI design's §9, item 9).
   * survey-api sends the account's `roles[]` beside it too; it is not typed here and not drawn —
   * the user card is where an account's roles are read.
   */
  kind: AccountKind;
  status: AccountStatus;
  /**
   * How the account acts for this participant (SR-416, F1): `self` — its own account, the one
   * relation staff's link writes; `contact` — a point of contact, written by an accepted
   * invitation alone. survey-api counts a `contact` as an account in the hub's household and
   * accounts, the activity and every list filter, but **not** in the link's 409
   * `participant-has-account`, the sightings, the registration runs or the reopen mail — so the
   * hub's "has an account" is the `self` links' (`ownAccounts`). A spelling this app does not
   * know is drawn as spelled.
   */
  relation: AccountRelation;
  /** When survey-api learned the owner receives mail at `email` — `IAdminAccountDetail`'s
   *  member under the same name (block 6, R16). Null is "not confirmed", never "no address". */
  emailVerifiedAt: Nullable<string>;
  activity: IAdminAccountActivity;
  /**
   * Every participant the account acts for, **this one included**, so a person's hub links to
   * the firm it registered. `AdminParticipantRefDto` — the same record every admin payload names
   * a participant with, so it is `IParticipantRef` rather than a second mirror of one DTO.
   */
  participants: IParticipantRef[];
}

/**
 * A registration run that resolved this participant — compact. The rest of a run (its number,
 * lookup, refusal, attempts) is the registration queue's row.
 */
export interface IAdminParticipantRegistration {
  runId: string;
  /** Where the run started — null for a person's run (SR-487), which names `numbers`. */
  survey: Nullable<string>;
  /** Where it landed — `survey` unless a redirect moved it (RIA_FIRM → TEAMS, or a team's
   *  answers TEAMS → RIA_FIRM); null for a person's run. */
  destinationSurvey: Nullable<string>;
  /** A person's run's declared numbers, `[]` for none; null for a survey's run. Absent from an
   *  API before SR-487. */
  numbers?: Nullable<RegistrationNumber[]>;
  status: RegistrationRunStatus;
  step: RegistrationStep;
  /** The name FINRA or the SEC returned for the run's number (SR-215) — null where no registry
   *  named anyone. As the registry spelled it. */
  registryName: Nullable<string>;
  startedAt: string;
  finishedAt: Nullable<string>;
}

/**
 * A firm of the SEC ADV register one of the participant's numbers names — survey-api's
 * `AdminParticipantSecAdvFirmDto` (its SR-214, register browse plan R6): its `SEC_NUMBER` the
 * firm's SEC number, or its `ORG_CRD` the firm's CRD. The firm's card is
 * `/admin/sec-adv/firms/{firmCrd}` (`sec-adv-contracts.ts` types it).
 *
 * A match is **not a verification**: a number naming a register firm says the number exists at
 * the SEC, not that the participant is that firm. "Verify number" stays a person's decision.
 */
export interface IAdminParticipantSecAdvFirm {
  firmCrd: string;
  secNumber: string;
  legalName: string;
  /** As the feed spells it — `Registered`, `ERA`. */
  firmType: string;
  /** The identifier types that named the firm, `ORG_CRD` / `SEC_NUMBER`, in ordinal order —
   *  as the firm card's participants carry them. */
  matchedBy: string[];
}

/**
 * One participant, whole — `AdminParticipantDetailDto`. The row's eight members, then what no
 * other route answers. Its responses and notes are not in it (survey-api's R2): the hub document
 * below carries the household's beside its cards, which is where this app reads them now.
 *
 * Every list is `[]`, never null.
 */
export interface IAdminParticipantDetail extends IAdminParticipantRow {
  identifiers: IAdminParticipantIdentifier[];
  facts: IAdminParticipantFact[];
  pendingLookups: IAdminParticipantPendingLookup[];
  accounts: IAdminParticipantAccount[];
  registrations: IAdminParticipantRegistration[];
  /**
   * Every register firm one of the participant's numbers names, ordered by legal name
   * (ordinal) then CRD — appended last by survey-api, so the card stays the row's superset.
   * A list, because two numbers can name two firms: an ERA that registered, a mistyped CRD.
   * `[]` when none does, which on a register that holds nothing is every participant.
   */
  secAdvFirms: IAdminParticipantSecAdvFirm[];
  /** Where the team is routed, as staff read it — null for a participant that is not a team to
   *  survey-api's routing read (not an org with a Teams response or a decision; the Development
   *  seed's team is one, its B2). An archived team still carries it. */
  teamRouting: Nullable<IAdminTeamRouting>;
  /** The archive stamp — null for a participant nobody archived, which is every one but a
   *  retired team. */
  archive: Nullable<IAdminParticipantArchive>;
  /**
   * The newest app write of a value staff own — a staff field set or cleared, a figure cell, a
   * ranking placement set or removed, a figure freeze (SR-222 block 10, the roadmap's B6) — as
   * survey-api counts it. **Not counted:** notes, answer corrections, photos, and every value the
   * migration wrote, which are Salesforce's edits. Null where no such write exists; `by` null
   * where the write names no account. A team's edit names the account that made it, never a
   * member (survey-api's R8). **Absent** from a survey-api before block 10, which states nothing:
   * the page then draws no staff edit at all, never "none" (`secAdvFirms`' rule).
   */
  lastInternalEdit?: Nullable<IAdminInternalEdit>;
  /**
   * The addresses invited to sign in for the participant as its point of contact (SR-416's
   * B5-16, F3), newest first — `[]` where none was. A submit naming a point-of-contact address
   * writes a `pending` one; its token is mailed by the worker, so none is accepted on `local`.
   * A `pending` row stays `pending` past its `expiresAt` until a later submit marks it `expired`.
   * **Absent** from a survey-api before SR-416. Drawn under Account & access
   * (`person-hub/invitations.tsx`).
   */
  invitations?: IAdminParticipantInvitation[];
  /** The participant's number checks (SR-487, PFR §4.3), newest first — `[]` where none was
   *  raised. **Absent** from a survey-api before SR-487. */
  checks?: IIdentifierCheck[];
  /** Each `listed` pin, not archived, of a survey about the participant's kind, and whether its
   *  access list admits the participant (SR-487, PFR §6). **Absent** before SR-487. */
  cycleAccess?: IAdminParticipantCycleAccess[];
}

/** One `listed` pin on a participant's card — `AdminParticipantCycleAccessDto`. `addedAt` and
 *  `addedBy` are null while it is not admitted; the pin's `/access` routes add or remove it. */
export interface IAdminParticipantCycleAccess {
  cycleCode: string;
  surveyCode: string;
  admitted: boolean;
  addedAt: Nullable<string>;
  addedBy: Nullable<IAdminVerifier>;
}

/** `AdminParticipantInvitationDto`'s `status` — open, an unknown one drawn as spelled. */
export type InvitationStatus =
  | 'pending'
  | 'accepted'
  | 'superseded'
  | 'revoked'
  | 'expired'
  | (string & {});

/** An address invited to sign in for a participant — `AdminParticipantInvitationDto`, the token
 *  left out. `accountId` is the account an acceptance linked (as a `contact`). */
export interface IAdminParticipantInvitation {
  invitationId: string;
  email: string;
  /** `contact`, or `assistant` for an advisor's invitation of an assistant (SR-487). */
  relation: AccountRelation;
  /** `poc_answer` — a submitted response named the address; `staff` — staff invited it;
   *  `respondent` — the advisor invited an assistant (SR-487). */
  source: 'poc_answer' | 'staff' | 'respondent' | (string & {});
  /** The response whose point-of-contact answer wrote it — null for staff's. */
  responseId: Nullable<string>;
  status: InvitationStatus;
  expiresAt: string;
  createdAt: string;
  decidedAt: Nullable<string>;
  accountId: Nullable<string>;
}

/** `RecordTypes` — the record page a participant opens on (SR-391). */
export type RecordType = 'person' | 'team' | 'firm' | 'org';

/** `AdminInternalEditDto` — when staff last edited a participant's values, and who. */
export interface IAdminInternalEdit {
  at: string;
  by: Nullable<IAdminVerifier>;
}

/**
 * A team's routing as staff read it — `AdminTeamRoutingDto`: the team's own read
 * (`MyTeamRoutingDto`, which the catalogue reads) plus who made the decision in force and the
 * staff note, which the team is never shown. So it **inherits** the team's shape rather than
 * spelling it a second time, the row-and-detail precedent above.
 *
 * `decidedAt`, `decidedBy` and `note` are set only while a decision holds the outcome — the
 * team's own answers (a registration's, or its routing page's) or a decision of Veritant's; `null`
 * for a team its firm alone routes, and for one not routed yet. `decidedBy` is the account that
 * made it — the team's own for its answers (the account its registration created, or the one
 * that answered on its routing page), a member of staff's for Veritant's — and null only where
 * survey-api holds no account for it. `answers` is spelled
 * as survey-api stores it, keyed by question code: no route serves the questions' texts outside
 * a question (the plan's B4), so nothing here labels them.
 */
export interface IAdminTeamRouting extends IMyTeamRouting {
  decidedBy: Nullable<IAdminVerifier>;
  note: Nullable<string>;
}

/**
 * A team's archive stamp — `AdminParticipantArchiveDto`. Archiving releases the team's
 * `TEAM_LEAD_CRD`, and `teamLeadCrd` is the value it released (null for a team that held none),
 * which a restore adds back **unverified**.
 */
export interface IAdminParticipantArchive {
  archivedAt: string;
  archivedBy: IAdminVerifier;
  teamLeadCrd: Nullable<string>;
}

// ---------------------------------------------------------------------------
// GET /admin/participants/{id}/hub (SR-215, person hub §4)
// ---------------------------------------------------------------------------

/**
 * One page for a person — `AdminParticipantHubDto`: the participant opened, and every other
 * participant an account of it acts for (**one level**: the accounts of the one opened), each a
 * whole card; then the household's responses (`updatedAt` newest first), notes (newest first)
 * and pending lookups (the opened card's, then each member's). **Unpaged** — bounded by the
 * account links — so the hub pages its lists itself.
 *
 * `pendingLookups` repeats what the cards carry, for the household at once: it is what the
 * poll watches. `disclosures` is one summary per member (SR-216), in the order of the cards —
 * survey-api appended it last, and the cards are unchanged.
 */
export interface IAdminParticipantHub {
  participant: IAdminParticipantDetail;
  /** Every **other** member; `[]` for a household of one. A member gone by its own read is left
   *  out by survey-api. */
  household: IAdminParticipantDetail[];
  responses: IOversightResponseRow[];
  notes: IStaffNote[];
  pendingLookups: IAdminParticipantPendingLookup[];
  /** One entry per member, the opened participant first, then the household as the cards are. */
  disclosures: IAdminMemberDisclosures[];
  /**
   * Each member's staff-field record — asked for with `?include=internal` (survey-api's SR-222,
   * staff fields §6) — one per member **holding a response**, in the cards' order, each narrowed
   * to the surveys of that member's responses. **Optional: absent from an API without it** (the
   * query parameter is ignored there), which draws no Veritant research section rather than an
   * empty one.
   */
  internal?: IParticipantInternalRecord[];
  /**
   * The opened person's teams and firms (record search §3.7–§3.8), each with its responses — `[]`
   * when the participant opened is not a person. **Absent** from an API before SR-414, which
   * Related records answers from each person's roster read instead (the plan's D4), never as
   * "named on no team".
   */
  affiliations?: IAdminHubAffiliation[];
}

// ---------------------------------------------------------------------------
// The disclosures of a participant's CRD set (SR-216, disclosures spec §6.2–§6.5)
// ---------------------------------------------------------------------------
//
// A participant's **CRD set** is survey-api's (`DisclosureScope`): its `CRD` identifiers that are
// a CRD under `Crds.TryCanonicalize` (1–10 digits, leading zeros dropped), and the CRDs of the
// migrated disclosures filed under its Salesforce survey and team records — how a team, which
// holds no CRD identifier, gets its members'. Nothing here computes it, canonicalises a CRD,
// sorts, or counts: the set, the groups, their order and the counts are all sent.
//
// Kinds, origins, Salesforce objects and section and field codes are `string`, not closed
// unions — the activity feed's rule: a value this app has no words for is still a record, and is
// drawn as spelled. The values the label tables know are named beside those tables.

/** One member's disclosures at a glance — `AdminMemberDisclosuresDto`. `summary` is null when
 *  the member's CRD set is empty: no CRD survey-api looks disclosures up by, and no migrated
 *  disclosure filed under its records. */
export interface IAdminMemberDisclosures {
  participantId: string;
  summary: Nullable<IAdminDisclosureSummary>;
}

/**
 * `AdminDisclosureSummaryDto`, over the member's CRD set: `checked` is false when no CRD of it
 * has been looked up (has no subject); `refreshedAt` is the newest stamp, null when nothing was
 * checked; the counts are over the set's disclosures, live and all.
 */
export interface IAdminDisclosureSummary {
  checked: boolean;
  refreshedAt: Nullable<string>;
  liveCount: number;
  totalCount: number;
}

/**
 * One CRD of the set and its disclosures — `AdminDisclosureGroupDto`. survey-api puts the
 * participant's own CRD first, then sorts by advisor name (nulls last), then by CRD; the wire
 * carries no `own`, so a person's own CRD and one the ETL parked under their Salesforce record
 * read alike (`advisorName: null` both). `advisorName` is the newest source's, as Salesforce
 * shows it. Within the group live disclosures come first, then the newest event, undated last.
 */
export interface IAdminDisclosureGroup {
  crd: string;
  advisorName: Nullable<string>;
  /** Null for a CRD never looked up. */
  subject: Nullable<IAdminDisclosureSubject>;
  disclosures: IAdminDisclosure[];
  /** The CRD's U4 at a glance (survey-api's SR-222, staff fields §7). **Optional: absent from an
   *  API without it**, which draws no head rather than one with nothing to say. */
  u4?: IAdminDisclosureU4;
}

/**
 * A CRD's U4 at a glance — `AdminDisclosureU4Dto`, every member present and null where there is
 * nothing to say. `liveByResolution` in the order sent, a null `resolution` its own row;
 * `newSinceLastClosedCycle` null when the participant has no closed cycle. The registry values
 * (`fullName`, `firmNames`, `firmIds`, `lastError`, `firms`) are null on every group but the one
 * whose CRD the participant's latest `ok` fact answered — BrokerCheck's or IAPD's since SR-396 —
 * and `lastError` is `""` on an `ok` answer. Nothing here counts any of it.
 */
export interface IAdminDisclosureU4 {
  liveByResolution: { resolution: Nullable<string>; count: number }[];
  newSinceLastClosedCycle: Nullable<number>;
  refreshedAt: Nullable<string>;
  refreshedBy: Nullable<string>;
  fullName: Nullable<string>;
  firmNames: Nullable<string>;
  firmIds: Nullable<string>;
  lastError: Nullable<string>;
  /**
   * The pair's current firms, each with the registries whose answer lists it (SR-396 B8) — drawn
   * in place of `firmNames` and `firmIds`. **Optional: absent from an API before SR-396**, which
   * draws the two strings as before, never "no current firm".
   */
  firms?: Nullable<IAdminDisclosureFirm[]>;
}

/** A current firm of the pair — `AdminDisclosureFirmDto`. `listedBy` names the registries
 *  (`brokercheck`, `iapd`) whose answer lists it, in survey-api's order. */
export interface IAdminDisclosureFirm {
  firmId: Nullable<number>;
  firmName: Nullable<string>;
  listedBy: string[];
}

/** The CRD's last check — `AdminDisclosureSubjectDto`. `refreshedBy` is an origin
 *  (`salesforce`, `brokercheck` or, since SR-396, `iapd`); `liveCount` is that stamp's, not the
 *  list's. */
export interface IAdminDisclosureSubject {
  refreshedAt: string;
  refreshedBy: string;
  liveCount: number;
}

/**
 * One disclosure — `AdminDisclosureDto`: the list members, then its **non-empty** sections in
 * Salesforce's layout order, its Salesforce copies (newest modified first) and its history
 * (newest first). `eventDate` is a day, `yyyy-MM-dd`.
 */
export interface IAdminDisclosure {
  id: string;
  eventDate: Nullable<string>;
  type: Nullable<string>;
  resolution: Nullable<string>;
  allegations: Nullable<string>;
  /** Listed by a registry as last observed — survey-api keeps it true exactly while `listedBy`
   *  names one (its CHECK). */
  live: boolean;
  /**
   * The registries whose latest answer lists the disclosure (SR-396 B5), `brokercheck` before
   * `iapd`; `[]` once it is gone. **Optional: absent from an API before SR-396**, where the chip
   * is drawn from `live` alone — never as "listed by no registry".
   */
  listedBy?: string[];
  sections: IAdminDisclosureSection[];
  sources: IAdminDisclosureSource[];
  changes: IAdminDisclosureChange[];
}

/** One section of Salesforce's page — `code` is survey-api's (`DisclosureSections`). */
export interface IAdminDisclosureSection {
  code: string;
  fields: IAdminDisclosureField[];
}

/**
 * One member of `DisclosureFields`, by its snake_case code. Present only with a value: text that
 * is not blank, or a flag that is `true`. `value` is a string, a `yyyy-MM-dd` string
 * (`event_date`), a number (the four amounts and the two category types) or `true`.
 */
export interface IAdminDisclosureField {
  code: string;
  value: string | number | boolean;
}

/** A Salesforce copy the disclosure was merged from — `AdminDisclosureSourceDto`. `sfObject` is
 *  `Disclosure__c` (a survey's) or `Team_Disclosure__c` (a team's) today. */
export interface IAdminDisclosureSource {
  sfObject: string;
  sfId: string;
  sfParentId: Nullable<string>;
  deletedUpstreamAt: Nullable<string>;
}

/**
 * One journalled change — `AdminDisclosureChangeDto`. `kind` is `created`, `changed`, `gone` or
 * `returned` today, `origin` `salesforce`, `brokercheck` or (SR-396) `iapd`. `changed` is the
 * moved members by code, each `{old, new}` as stored — null for a `created` change and for a bare
 * `gone`. Its values are the fields' own JSON, so `false` and `null` can appear in it, which no
 * section field carries.
 */
export interface IAdminDisclosureChange {
  kind: string;
  origin: string;
  occurredAt: string;
  changed: Nullable<Record<string, { old: unknown; new: unknown }>>;
}

// ---------------------------------------------------------------------------
// GET /admin/participants/{id}/salesforce-tasks (SR-216, disclosures spec §6.5)
// ---------------------------------------------------------------------------

/**
 * One archived Salesforce `Task` filed under one of the participant's Salesforce records —
 * `AdminSalesforceTaskDto`, newest `createdAt` first, then `sfId` descending. `dueDate` is a day.
 * `owner` is a queue's or a user's name and `createdBy` a user's, each null where the archive
 * holds no such record. `disclosureId` is set when the task's Comments were one Lightning record
 * URL naming a known disclosure source, and `description` is then null; otherwise
 * `description` holds the Comments as stored — a URL into another org among them — or null.
 */
export interface IAdminSalesforceTask {
  sfId: string;
  subject: Nullable<string>;
  status: Nullable<string>;
  createdAt: string;
  completedAt: Nullable<string>;
  dueDate: Nullable<string>;
  owner: Nullable<string>;
  createdBy: Nullable<string>;
  disclosureId: Nullable<string>;
  description: Nullable<string>;
}

// ---------------------------------------------------------------------------
// GET /admin/participants/{id}/activity (SR-215, person hub §8)
// ---------------------------------------------------------------------------

/** Which table an activity row is from. Each numbers its own rows, so `(source, id)` is the
 *  key, never `id` alone. */
export type ActivitySource = 'account' | 'audit';

/** Who acted. `email` is null for an account since removed — the id outlives the row. */
export interface IAdminActivityActor {
  accountId: string;
  email: Nullable<string>;
}

/**
 * One event of the household's feed — `AdminActivityRowDto`: its accounts' `account_event`
 * rows, every type, and its members' participant-scoped `audit_event` rows, newest first.
 */
export interface IAdminActivityRow {
  source: ActivitySource;
  id: number;
  at: string;
  /** The stored event type, as stored — not a closed set here: a kind this app has no words
   *  for is still an event, and is drawn by its spelling. */
  kind: string;
  /** Null when no account acted. */
  actor: Nullable<IAdminActivityActor>;
  /** The event's stored document, or null where it has none. survey-api keeps addresses, IPs
   *  and user agents out of it. */
  detail: Nullable<unknown>;
}

// ---------------------------------------------------------------------------
// Why a row matched, and a person's teams and firms (SR-414, record search §6, §3.7)
// ---------------------------------------------------------------------------

/**
 * One reason a search row matched — `AdminSearchMatchDto` (record search §6, D9): the field, the
 * stored value or a note's snippet, and the rank of the hit it explains. A row carries at most
 * ten, best rank first, in survey-api's order — nothing here sorts, filters or counts them.
 *
 * `field` is an open set (`SearchMatchFields.All` names 34 today), a `string` here and drawn as
 * spelled where `search-matches.ts` has no words for it. `participantId` is whose value it is —
 * null for an account's address and for a slot; `idType` is set on `identifier` and
 * `salesforce_id`, `slot` on the four roster fields (`member_name`, `member_email`, `member_crd`,
 * `member_of`), `source` on `notes` and `forbes_id`. A `notes` value is a snippet, plain text —
 * never markup.
 */
export interface ISearchMatch {
  field: string;
  value: string;
  rank: number;
  participantId: Nullable<string>;
  idType: Nullable<string>;
  slot: Nullable<ISearchMatchSlot>;
  source: Nullable<ISearchMatchSource>;
}

/** The team or firm a roster match's slot is on — `AdminMatchSlotDto`: the record, its type, and
 *  the slot's number and role (`lead`, `member`, `principal` — open, drawn as spelled). */
export interface ISearchMatchSlot {
  participantId: string;
  displayName: string;
  recordType: RecordType;
  slot: number;
  role: string;
}

/** The internal field a `notes` or `forbes_id` match is a value of — `AdminMatchSourceDto`. */
export interface ISearchMatchSource {
  fieldCode: string;
  label: string;
}

/**
 * One of a person's teams and firms — `AdminAffiliationDto` (record search §3.7): reached by an
 * account of the person acting for it (`viaAccount`), by a roster slot that resolves to the person
 * (`slot`, `role`, `resolvedBy` — the best such slot), or both; `slot`, `role` and `resolvedBy`
 * are null when only an account reaches it. `surveyCodes` are the team's or firm's own surveys.
 * In survey-api's order. `resolvedBy` is the roster's open set, `email` among it since SR-414.
 */
export interface IAffiliation {
  participantId: string;
  displayName: string;
  recordType: RecordType;
  surveyCodes: string[];
  viaAccount: boolean;
  slot: Nullable<number>;
  role: Nullable<string>;
  resolvedBy: Nullable<string>;
}

/** An affiliation on the person's page — `AdminHubAffiliationDto`: plus the team's or firm's
 *  responses as the hub reads its own, `updatedAt` newest first. */
export interface IAdminHubAffiliation extends IAffiliation {
  responses: IOversightResponseRow[];
}

// ---------------------------------------------------------------------------
// GET /admin/people (SR-215, person hub §6; SR-414, record search §3.1, §3.6)
// ---------------------------------------------------------------------------

/** What a People row is (`PeopleRowType`): an account with the persons it acts for, a person no
 *  account acts for, or an unregistered member named on a roster (record search §3.1). */
export type PersonRowType = 'account' | 'participant' | 'roster';

/**
 * What every People row carries — `AdminPersonRowDto`. Since SR-414 the list is **persons only**
 * (record search D3): no team, firm or org is a row or one of a row's `participants`, which
 * `affiliations` name instead. `key` is the row's own — the account's id, the participant's id or
 * the roster key — and unique on the page. `displayName` and `profile` are the row's person, who
 * comes first in `participants` (a roster row's profile is all null). `surveys` are the distinct
 * codes of the persons' responses **and** their affiliations', ordinal. `matchedIn` is the arm of
 * `q` the row matched in, typed and read by nothing — `matches` says more. `affiliations` and
 * `matches` are **absent** from an API before SR-414 (the plan's D4): read `?? []`.
 */
interface IAdminPersonRowBase {
  rowType: PersonRowType;
  key: string;
  displayName: string;
  profile: IAdminParticipantProfile;
  participants: IAdminParticipantListItem[];
  surveys: string[];
  /** The account's instant, the person's, or a roster row's earliest slot's `as_of`. */
  createdAt: string;
  lastLoginAt: Nullable<string>;
  matchedIn?: Nullable<string>;
  affiliations?: IAffiliation[];
  /** Why `q` found the row, at most ten, best first; `[]` without `q`. */
  matches?: ISearchMatch[];
}

/** An account and every person it acts for, the person first. */
export interface IAdminAccountPersonRow extends IAdminPersonRowBase {
  rowType: 'account';
  accountId: string;
  email: string;
  /** The account's kind (SR-228), where `role` was — as on the hub's account (§9, 9); its
   *  `roles[]`, sent beside it, are not read here. */
  kind: AccountKind;
  status: AccountStatus;
  participantId: string;
}

/** A person no account acts for: its four account members null. */
export interface IAdminParticipantPersonRow extends IAdminPersonRowBase {
  rowType: 'participant';
  accountId: null;
  email: null;
  kind: null;
  status: null;
  participantId: string;
}

/**
 * An unregistered member (record search §3.6) — slots no participant resolves to, on a team or
 * firm that is not archived, grouped by CRD, else email, else each slot alone. Sent only with
 * `include=roster` or `account=unregistered`. No participant, no account: `participantId` null,
 * `participants` empty, every account member and profile member null. Its `key` is the roster
 * key, which `GET …/people/roster/{key}` opens.
 */
export interface IAdminRosterPersonRow extends IAdminPersonRowBase {
  rowType: 'roster';
  accountId: null;
  email: null;
  kind: null;
  status: null;
  participantId: null;
  roster: IAdminPersonRoster;
}

/** A roster row's own part — `AdminPersonRosterDto`: the group's canonical CRD, or null, and its
 *  distinct emails, newest first. */
export interface IAdminPersonRoster {
  crd: Nullable<string>;
  emails: string[];
}

/**
 * One person on the People list — `AdminPersonRowDto`, by its `rowType`. Every reader narrows on
 * it: `participantId` is null on a roster row alone.
 */
export type IAdminPersonRow =
  | IAdminAccountPersonRow
  | IAdminParticipantPersonRow
  | IAdminRosterPersonRow;

/** Over the row's participants, each an `EXISTS`: one holds a confirmed number; one holds none;
 *  one's owner denied a number. A row can be under more than one. */
export type PeopleVerification = 'verified' | 'unverified' | 'denied';

/** The status of the **newest** `brokercheck.*` fact across the row's participants, or
 *  `never_asked` when none has one. */
export type PeopleRegistry = 'never_asked' | 'ok' | 'not_found' | 'error';

/** The row's account: none acts for the person; one holding no identity at the IdP (SR-476); no
 *  successful sign-in under its address; or — `unregistered`, People's alone — the roster rows
 *  alone (SR-414). Teams and Firms refuse `unregistered` with a 400. */
export type PeopleAccount = 'none' | 'not_linked' | 'never_signed_in' | 'unregistered';

/** Over the row's participants, each an `EXISTS` over the teams survey-api routes, archived ones
 *  left out: one routed so, or — `unknown` — one it routes nowhere yet, the manual queue. */
export type PeopleTeamRouting = TeamRoutingOutcome | 'unknown';

/** Over the row's teams, as `PeopleTeamRouting`: one whose routing rests on this — its firm alone
 *  is the sample staff spot-check, `none` a team nothing has routed. */
export type PeopleTeamRoutingSource = TeamRoutingSource;

/** Over the row's CRD sets together (SR-216, disclosures spec §6.6): `live` — at least one live
 *  disclosure; `clean` — a CRD looked up and none live; `unchecked` — a CRD and none looked up.
 *  A row with a live disclosure and no subject is **both** `live` and `unchecked`. */
export type PeopleDisclosures = 'live' | 'clean' | 'unchecked';

/** `name` is the person's last name, first name and display name, ordinal; `last_change` the
 *  newest `updatedAt` of the row's responses. Nulls last either way. */
export type PeopleSortKey = 'name' | 'created' | 'last_login' | 'last_change';

export interface IPeopleSort {
  key: PeopleSortKey;
  descending: boolean;
}

/**
 * The People search. Every filter is optional and none is judged here: `q` is sent as typed
 * (trimmed there, blank absent; at most eight words and 200 characters, else a 400 at `q` —
 * SR-414's engine, roster slots included, ranked), and an unknown code or value is survey-api's
 * 400 `invalid-query`, every problem named at once. `include: 'roster'`, its one value, adds the
 * roster rows; the screen derives it from `q` and never reads it from the address.
 *
 * `survey` repeats and is a union; `cycle` is one code. `createdSince` is an ISO instant — the
 * screen sends a day as that day's UTC midnight. Absent `sort` is survey-api's ranking for `q`,
 * then newest first. `q` matches a team's `TEAM_LEAD_CRD` exactly, as any other number.
 *
 * `teamLeadShared` narrows to a team whose slot-1 CRD is another team's `TEAM_LEAD_CRD` — the
 * queue where staff archive a duplicate; survey-api reads `false` as not narrowing. It is a
 * `bool` there, so a spelling that is neither is refused by its binder before the route runs —
 * `invalid-body`, not `invalid-query`, and naming `request` rather than the parameter, since the
 * binder cannot say which value it was.
 */
export interface IPeopleQuery {
  q?: string;
  survey?: string[];
  cycle?: string;
  verification?: PeopleVerification;
  registry?: PeopleRegistry;
  account?: PeopleAccount;
  disclosures?: PeopleDisclosures;
  teamRouting?: PeopleTeamRouting;
  teamRoutingSource?: PeopleTeamRoutingSource;
  teamLeadShared?: boolean;
  createdSince?: string;
  include?: 'roster';
  /** The view card (SR-514); absent is every row. */
  card?: PeopleCard;
  sort?: IPeopleSort;
  page?: number;
  pageSize?: number;
}

/** People's view cards (SR-514): `account` the rows an account is, `unregistered` the rest —
 *  persons no account acts for and, when listed, roster members; `roster` the roster rows alone,
 *  searched or not (SR-518). */
export type PeopleCard = 'all' | 'account' | 'unregistered' | 'roster';

/** `GET /admin/people/counts`: each card's rows under the list's search and filters, never its
 *  card, sort or page — and never `roster`, which only its own list counts. `signals` is empty:
 *  People draws none. */
export interface IPeopleCounts {
  cards: Partial<
    Record<Exclude<PeopleCard, 'roster'>, { count: number; signals: Record<string, number> }>
  >;
}

// ---------------------------------------------------------------------------
// GET /admin/teams, GET /admin/firms (SR-414, record search §3.2–§3.5)
// ---------------------------------------------------------------------------

/** The account acting for a record that was linked to it first — `AdminRecordAccountDto`;
 *  `status` as a People row spells it. */
export interface IRecordAccount {
  accountId: string;
  email: string;
  status: AccountStatus;
}

/** A team's routing on its row — `AdminRecordRoutingDto`: `outcome` null while unknown, and what
 *  it rests on. */
export interface IRecordRouting {
  outcome: Nullable<TeamRoutingOutcome>;
  source: TeamRoutingSource;
}

/** The record's response changed last — `AdminLatestResponseDto`; `pathCode` null on a response
 *  without a path. */
export interface ILatestResponse {
  responseId: string;
  cycleCode: string;
  status: ResponseStatus;
  pathCode: Nullable<string>;
  updatedAt: string;
}

/**
 * What a Teams and a Firms row share: the participant as the picker's row spells it, its
 * earliest-linked account, its survey codes, its newest response, its instant, and why `q` found
 * it (`[]` without `q`). New routes, so no member is absent from an older API.
 */
interface IAdminRecordRowBase {
  participantId: string;
  displayName: string;
  photoUri: Nullable<string>;
  identifiers: IAdminParticipantListIdentifier[];
  profile: IAdminParticipantProfile;
  account: Nullable<IRecordAccount>;
  surveys: string[];
  latestResponse: Nullable<ILatestResponse>;
  createdAt: string;
  matches: ISearchMatch[];
}

/** One team — `AdminTeamRowDto`, archived ones included: its roster's `members` exactly as
 *  `GET …/roster` spells them, and its routing (null for a team survey-api routes nowhere). */
export interface IAdminTeamRow extends IAdminRecordRowBase {
  members: IAdminRosterMember[];
  routing: Nullable<IRecordRouting>;
}

/** One firm or org — `AdminFirmRowDto`: its `recordType` (`firm` or `org`), its roster's
 *  `principals`, and the register firms its numbers name, as its card lists them. */
export interface IAdminFirmRow extends IAdminRecordRowBase {
  recordType: RecordType;
  principals: IAdminRosterMember[];
  secAdvFirms: IAdminParticipantSecAdvFirm[];
}

/** `RecordListRoster`: a slot not identified (`unresolved`), or no slot named (`empty`). */
export type RecordListRoster = 'unresolved' | 'empty';

/** `RecordListSecAdv`: a firm one of whose numbers names a register firm, or none does. */
export type RecordListSecAdv = 'linked' | 'unlinked';

/** `RecordListSortKey` — People's without `last_login`, which is a 400 here. */
export type RecordListSortKey = 'name' | 'created' | 'last_change';

export interface IRecordListSort {
  key: RecordListSortKey;
  descending: boolean;
}

/**
 * The filters Teams and Firms share, People's vocabularies reused by name and read over the
 * record itself (record search §3.4). `account` takes no `unregistered` — no record is a roster
 * row — and nothing is judged here: survey-api's 400 says so.
 */
interface IRecordListQuery {
  q?: string;
  cycle?: string;
  verification?: PeopleVerification;
  registry?: PeopleRegistry;
  account?: PeopleAccount;
  disclosures?: PeopleDisclosures;
  roster?: RecordListRoster;
  createdSince?: string;
  sort?: IRecordListSort;
  page?: number;
  pageSize?: number;
}

/** The Teams search — plus People's three team filters, Teams' own since SR-414. */
export interface ITeamsQuery extends IRecordListQuery {
  teamRouting?: PeopleTeamRouting;
  teamRoutingSource?: PeopleTeamRoutingSource;
  teamLeadShared?: boolean;
  /** The view card (SR-514); absent is All. */
  card?: TeamCard;
}

/** The Firms search — plus `secAdv` and the record type. */
export interface IFirmsQuery extends IRecordListQuery {
  secAdv?: RecordListSecAdv;
  recordType?: FirmRecordType;
}

/** Teams' view cards (SR-514): wires, independent, ria and unrouted count live teams only. */
export type TeamCard = 'all' | 'wires' | 'independent' | 'ria' | 'unrouted' | 'archived';

/** A Firms row's record type, as `recordType=` narrows to it. */
export type FirmRecordType = 'firm' | 'org';

/** `GET /admin/teams/counts`: each card's teams under the list's search and filters, never its
 *  card, sort or page. `signals` is empty: Teams draws none. */
export interface ITeamCounts {
  cards: Partial<Record<TeamCard, { count: number; signals: Record<string, number> }>>;
}

/** The fields `GET /admin/teams/facets` counts the values of. */
export type TeamFacetField = 'teamRouting' | 'teamRoutingSource' | 'roster' | 'cycle';

/** The fields `GET /admin/firms/facets` counts the values of. */
export type FirmFacetField = 'recordType' | 'secAdv' | 'roster' | 'cycle';

/** A facets read: the field's values among the rows the other filters keep, most common first. */
export interface IRecordFacets {
  field: string;
  values: { value: string; count: number }[];
}

// ---------------------------------------------------------------------------
// GET /admin/people/roster/{rosterKey} (SR-414, record search §3.6)
// ---------------------------------------------------------------------------

/**
 * A roster row's card — `AdminRosterCardDto`: the key, the name, the CRD and the emails (newest
 * first), and its slots, newest `asOf` first. survey-api answers a bodiless 404 once no
 * unresolved slot answers to the key — the member registered, or Veritant linked them.
 */
export interface IRosterCard {
  rosterKey: string;
  displayName: string;
  crd: Nullable<string>;
  emails: string[];
  slots: IRosterCardSlot[];
}

/**
 * One slot of a roster card — `AdminRosterCardSlotDto`: the team or firm it sits on, its survey
 * and response, the slot as it holds the member (each answer nullable, at least one set), and
 * `answers` — the member group's questions with this slot's answers alone, spelled as the
 * response detail spells them.
 */
export interface IRosterCardSlot {
  participantId: string;
  displayName: string;
  recordType: RecordType;
  surveyCode: string;
  responseId: string;
  slot: number;
  role: string;
  fullName: Nullable<string>;
  crd: Nullable<string>;
  email: Nullable<string>;
  asOf: string;
  answers: IOversightUsage[];
}

// ---------------------------------------------------------------------------
// The two writes addressed by the number itself
// ---------------------------------------------------------------------------

/**
 * A number, named by type and value — the body of both `…/registry-lookups` (the refresh) and
 * `…/identifier-verifications`. survey-api declares two records of this one shape and holds
 * them to one rule: both halves present after trimming, or a 400 naming the missing one. A
 * number the participant does not hold is 404 on either.
 *
 * Addressed by value rather than by `identifierId` on purpose: staff verify by reading the
 * registry in another tab, so the number is what they have in front of them.
 */
export interface IParticipantNumber {
  idType: string;
  value: string;
}

/**
 * What a verification answers — `AdminIdentifierDto`. It only ever sets: a number already
 * stamped answers with the stamp it has, and no route clears one.
 */
export interface IStampedIdentifier {
  participantId: string;
  identifierId: string;
  idType: string;
  value: string;
  verifiedAt: Nullable<string>;
  source: string;
}

// ---------------------------------------------------------------------------
// PUT /admin/participants/{id}/team-routing (SR-213's Teams block, its R7)
// ---------------------------------------------------------------------------

/**
 * Veritant's decision about a team's routing — the body of the `PUT`. An outcome wins over the
 * team's own answers and its firm; `null` clears a decision of Veritant's, and the team then falls
 * to **its firm**, not to its earlier answers, while `null` over no decision of Veritant's writes
 * nothing. `note` is trimmed there, blank absent, at most 10 000 characters (survey-api's
 * `MaxNoteLength`, the staff notes' limit) — judged there, a 400 `invalid-body` naming `note`.
 */
export interface ITeamRoutingDecision {
  outcome: Nullable<TeamRoutingOutcome>;
  note?: string;
}

// ---------------------------------------------------------------------------
// POST / DELETE /admin/participants/{id}/account-links (block 6, F3)
// ---------------------------------------------------------------------------

/**
 * What a link answers — `AdminAccountLinkDto`: the account now acting for the participant, and
 * how — `self` or `assistant`, the two relations staff's link writes (SR-487, PFR §8); a
 * `contact` is written by an accepted invitation alone. survey-api's 409
 * `participant-has-account` counts the `self` links only, so a participant whose only accounts
 * are points of contact can still be given its own.
 *
 * The body the route takes is `{ email }` — an **address**, because staff hold no account list
 * (`accounts.browse` is admin alone) and the hub names accounts by address. Trimmed there and
 * matched case-insensitively (`citext`).
 */
export interface IAdminAccountLink {
  accountId: string;
  email: string;
  relation: AccountRelation;
}

// ---------------------------------------------------------------------------
// GET /admin/registrations (A6)
// ---------------------------------------------------------------------------

/**
 * One registration attempt — `AdminRegistrationListItemDto`, runs that resolved nobody
 * included.
 *
 * A refusal takes two members to read, because two steps refuse and only one ends the run:
 * `status: abandoned` beside `resolution: number_taken` is a number already somebody's (the
 * anchor ends the run), while `refusal` is the completion's and leaves the run `in_progress`
 * for another address. Neither reached the registrant.
 */
export interface IAdminRegistrationRow {
  runId: string;
  /** Null for a person's run (SR-487), which names `numbers`. */
  survey: Nullable<string>;
  destinationSurvey: Nullable<string>;
  /** A person's run's declared numbers, `[]` for none; null for a survey's run. Absent from an
   *  API before SR-487. */
  numbers?: Nullable<RegistrationNumber[]>;
  status: RegistrationRunStatus;
  step: RegistrationStep;
  idType: Nullable<string>;
  value: Nullable<string>;
  lookup: Nullable<FactStatus>;
  /** The name the registry returned for the number (SR-215) — null where it named nobody. */
  registryName: Nullable<string>;
  /**
   * What the number resolved to against our own data — `IdentityOutcome` as the wire spells
   * it: `new_participant`, `existing_participant`, `number_taken`,
   * `address_belongs_to_another_account`. A string, not a union: it is read back out of a
   * stored `jsonb` event rather than a checked column.
   */
  resolution: Nullable<string>;
  /** Why the completion refused — `duplicate_address`, `address_belongs_to_another_account`,
   *  or, for a signed-in run, `identifier_already_yours`. Stored text, for the same reason. */
  refusal: Nullable<string>;
  attempts: number;
  redirected: boolean;
  /** Who the number named, when it named anyone — on a refusal too, which is the one place
   *  that holder is visible. Null for every new person. */
  participant: Nullable<IParticipantRef>;
  accountId: Nullable<string>;
  /** Null for every completion since the teams block: no registration opens a response. */
  responseId: Nullable<string>;
  startedAt: string;
  finishedAt: Nullable<string>;
  /** The newest step, not the newest edit — what "stuck" is measured against. */
  lastActivityAt: string;
}

/** The Registrations cards (SR-515), disjoint, summing to every attempt: `completed`; `refused` —
 *  not completed, and the number taken or the address refused; `attention` — the rest. */
export type RegistrationCard = 'attention' | 'refused' | 'completed';

/** The Registrations signals: `new` started after `since`, `stuck` stopped at "is this you?". */
export type RegistrationFlag = 'new' | 'stuck';

/** The orders `GET /admin/registrations` takes; none is `started:desc`. */
export type RegistrationSortKey = 'started' | 'last_activity';

/** `status` and `flag` repeat; none means **every** status — survey-api hides nothing from a
 *  reader who did not know to ask. A value it does not know is 400 `invalid-query`, by name. */
export interface IRegistrationQuery {
  q?: string;
  card?: RegistrationCard;
  flag?: RegistrationFlag[];
  /** The viewer's last visit — what `new` is counted since. */
  since?: string;
  status?: RegistrationRunStatus[];
  /** A survey code, or `person` for a person's run. */
  survey?: string;
  step?: string;
  /** `key` or `key:desc`. */
  sort?: string;
  page?: number;
  pageSize?: number;
}

/** `GET /admin/registrations/counts` — each card's count and its signals', under the list's search
 *  and filters, never its card, flags, sort or page. `new` is 0 without `since`. */
export interface IRegistrationCounts {
  cards: Partial<
    Record<'all' | RegistrationCard, { count: number; signals: Partial<Record<RegistrationFlag, number>> }>
  >;
}

/** The fields `GET /admin/registrations/facets` counts the values of. */
export type RegistrationFacetField = 'survey' | 'status' | 'step';

// ---------------------------------------------------------------------------
// GET /admin/participants/{id}/salesforce-records (SR-222, Salesforce archive spec §5)
// ---------------------------------------------------------------------------

/**
 * One archived Salesforce object that reaches the participant — `AdminSalesforceObjectDto`, the
 * route's answer **without** `sobject`: a bare array in survey-api's fixed order (the survey
 * objects, `Lead`, the survey `__History` objects, `Lumitra_Verification_Report__c`, `Task`,
 * `Note`). An object with no row for the participant is left out, never sent with `count: 0`.
 * `label` is the describe's, or the `sobject` itself where the archive holds no describe.
 */
export interface IAdminSalesforceObject {
  sobject: string;
  label: string;
  count: number;
}

/**
 * One archived record — `AdminSalesforceRecordDto`, an item of the route's page **with**
 * `sobject`, newest `modifiedAt` first and then `sfId` descending, as survey-api sorted it.
 * `modifiedAt` is an instant with an offset (`LastModifiedDate`; on a history row
 * `SystemModstamp`, else `CreatedDate`). `fields` holds only the fields with a value, in the
 * describe's order — survey-api leaves out a null or `""`, a field the describe lacks, a compound
 * and Salesforce's own bookkeeping, so an empty field is never sent.
 *
 * `fieldLabel` names a `*__History` row's changed `Field` as its parent object's describe labels
 * it; null where that describe or field is absent and on every other object. **Optional**: an API
 * older than its addition (survey-api `e5697922`) sends no member at all.
 */
export interface IAdminSalesforceRecord {
  sobject: string;
  sfId: string;
  modifiedAt: string;
  fields: IAdminSalesforceRecordField[];
  fieldLabel?: Nullable<string>;
}

/**
 * One field verbatim — `AdminSalesforceRecordFieldDto`. `type` is the describe's (`boolean`,
 * `currency`, `textarea`, `reference`, …): Salesforce's vocabulary, so a `string` rather than a
 * union this side would have to keep in step. `value` is the dump's JSON value, untouched.
 * `refName` is **omitted, not null**, unless the field is a `reference` whose record the archive
 * holds (`JsonIgnore(WhenWritingNull)`).
 */
export interface IAdminSalesforceRecordField {
  name: string;
  label: string;
  type: string;
  value: unknown;
  refName?: string;
}

// ---------------------------------------------------------------------------
// GET /admin/participants/{id}/roster (SR-222, survey-api's roster design §7)
// PUT and DELETE /admin/participants/{id}/roster/{slot}
// ---------------------------------------------------------------------------

/**
 * A participant's roster — `AdminRosterDto`, the one answer of all three routes. A team's or a
 * firm's own named slots are `members`; the teams and firms one of whose slots resolves to the
 * participant are `memberships`. Rows exist only on a team or a firm with a submitted or locked
 * `TEAMS` / `RIA_FIRM` response, and a slot resolves only to a person, so a person's `members` and
 * a team's or firm's `memberships` are empty by construction — a participant with neither answers
 * two empty lists, and one that does not exist a bodiless 404.
 */
export interface IAdminRoster {
  members: IAdminRosterMember[];
  memberships: IAdminRosterMembership[];
}

/**
 * One named slot — `AdminRosterMemberDto`, in slot order as sent. `fullName`, `crd` and `email`
 * are the team's answers, each nullable, at least one set; `crd` is canonical. `role` is `lead` (a
 * team's slot 1), `member` (a team's 2–5) or `principal` (a firm's), and `resolvedBy` is `staff`,
 * `salesforce`, `crd`, `email` (SR-414) or null — both open sets, a `string` here and drawn as spelled when unknown,
 * the rule for every vocabulary in this file. Resolution happens at read, first match winning:
 * staff's decision, Salesforce's link, a person holding the row's CRD, a person its email names.
 * **`member: null` with `resolvedBy: "staff"` is Veritant's decision of nobody; with
 * `resolvedBy: null` it is unresolved.**
 */
export interface IAdminRosterMember {
  slot: number;
  role: string;
  fullName: Nullable<string>;
  crd: Nullable<string>;
  email: Nullable<string>;
  member: Nullable<IAdminRosterMemberRef>;
  resolvedBy: Nullable<string>;
}

/** The person a slot resolves to — `AdminRosterMemberRefDto`. `photoUri` is the absolute public
 *  URL, as on the hub (SR-223), or null. */
export interface IAdminRosterMemberRef {
  participantId: string;
  displayName: string;
  photoUri: Nullable<string>;
  /** The member's record type — a `person`, since a slot resolves only to one. */
  recordType: RecordType;
}

/**
 * A team or firm one of whose slots resolves to the participant read — `AdminRosterMembershipDto`,
 * ordered by display name. `resolvedBy` is never null here: an unresolved slot names nobody. **An
 * archived team or firm is left out.** `surveyCode` is `TEAMS` or `RIA_FIRM`.
 */
export interface IAdminRosterMembership {
  participantId: string;
  displayName: string;
  surveyCode: string;
  slot: number;
  role: string;
  resolvedBy: string;
  /** The team's or firm's record type — which list's address a link to it names. */
  recordType: RecordType;
}
